import { useEffect, useState } from "react";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import * as auditLogApi from "../../api/auditLog";
import { AuditLogEntry } from "../../api/auditLog";
import { Table, TableColumn } from "../../components/ui/Table";
import { Button } from "../../components/ui/Button";

const ACTION_LABELS: Record<string, string> = {
  "user.invite": "Ha invitato un nuovo utente",
  "user.create_operator": "Ha creato un operatore",
  "user.disable": "Ha disabilitato un utente",
  "user.reset_password": "Ha reimpostato una password",
  "installation.update_costs": "Ha modificato i costi di un'installazione",
  "document.upload": "Ha caricato un documento",
  "document.publish": "Ha pubblicato un documento",
  "document.unpublish": "Ha nascosto un documento",
  "document.delete": "Ha eliminato un documento",
};

function describeDetails(entry: AuditLogEntry): string {
  const d = entry.details;
  if (!d) return "—";

  if (typeof d.target_email === "string") return d.target_email;
  if (typeof d.email === "string") return d.email;
  if (typeof d.titolo === "string") return d.titolo;
  if (d.ruolo) return `ruolo: ${d.ruolo}`;

  return Object.keys(d).length > 0 ? "Dettagli disponibili" : "—";
}

export default function AuditLog() {
  const { showToast } = useToast();
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  async function loadInitial() {
    setIsLoading(true);
    try {
      const { entries: list } = await auditLogApi.listAuditLog();
      setEntries(list);
      setHasMore(list.length === 50);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare il registro attività."), "error");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadMore() {
    if (entries.length === 0) return;
    setIsLoadingMore(true);
    try {
      const oldest = entries[entries.length - 1].created_at;
      const { entries: more } = await auditLogApi.listAuditLog(oldest);
      setEntries((prev) => [...prev, ...more]);
      setHasMore(more.length === 50);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare altre voci."), "error");
    } finally {
      setIsLoadingMore(false);
    }
  }

  const columns: TableColumn<AuditLogEntry>[] = [
    // Quando e Azione non si spengono: un registro senza il momento o senza
    // cosa e' successo non e' piu' un registro.
    {
      key: "created_at",
      header: "Quando",
      sempreVisibile: true,
      // Sulla stringa ISO, non sulla data formattata it-IT: in ordine
      // alfabetico "01/12/2025" verrebbe prima di "02/01/2026".
      sortValue: (e) => e.created_at,
      render: (e) => new Date(e.created_at).toLocaleString("it-IT"),
    },
    {
      key: "actor",
      header: "Chi",
      sortValue: (e) => (e.actor ? e.actor.full_name ?? e.actor.email : null),
      render: (e) => (e.actor ? e.actor.full_name ?? e.actor.email : "Utente eliminato"),
    },
    {
      key: "action",
      header: "Azione",
      sempreVisibile: true,
      // Sull'etichetta tradotta, cioe' su quello che si legge, non sul valore
      // grezzo dell'enum.
      sortValue: (e) => ACTION_LABELS[e.action] ?? e.action,
      render: (e) => ACTION_LABELS[e.action] ?? e.action,
    },
    {
      key: "details",
      header: "Dettagli",
      // NESSUN `sortValue`, di proposito: `details` e' un JSON, e ordinarlo
      // alfabeticamente non risponde a nessuna domanda. L'intestazione resta
      // inerte anche a tabella ordinabile — e' il caso per cui `sortValue` e'
      // opzionale invece che obbligatorio.
      render: describeDetails,
    },

    // --- Spente di default --------------------------------------------------
    // Accese insieme rendono possibile la domanda per cui un registro esiste:
    // "tutto quello che e' successo a QUESTO record", ordinando per entita'
    // invece di scorrere in ordine di tempo.
    {
      key: "entity_type",
      header: "Tipo entità",
      nascostaDiDefault: true,
      sortValue: (e) => e.entity_type,
      render: (e) => e.entity_type ?? <span className="text-ink/40">—</span>,
    },
    {
      key: "entity_id",
      header: "ID entità",
      nascostaDiDefault: true,
      sortValue: (e) => e.entity_id,
      render: (e) =>
        e.entity_id ? (
          <span className="font-mono text-xs">{e.entity_id}</span>
        ) : (
          <span className="text-ink/40">—</span>
        ),
    },
  ];

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-ink">Registro attività</h1>
        <p className="mt-1 text-sm text-ink/50">
          Chi ha fatto cosa sulle azioni sensibili — gestione utenti, costi, documenti privacy. Visibile solo agli
          admin.
        </p>
      </header>

      <Table
        columns={columns}
        // NIENTE `ordinabile` QUI, ed e' la differenza con Clienti e Sedi.
        //
        // `GET /api/audit-log` taglia lato server: `take: limit`, default 50
        // (max 200), e questa pagina non passa nessun limite. Sui dati di
        // sviluppo arrivano 50 righe su 265 in tabella.
        //
        // L'ordinamento del Table e' IN MEMORIA: ordinerebbe solo quelle 50, che
        // sono le piu' recenti. Chi cliccasse "Tipo entità" vedrebbe le
        // attivazioni in cima credendo di avere davanti tutte le attivazioni
        // registrate, mentre sono solo quelle fra le ultime 50 — l'ordinamento
        // che mente descritto nel commento sopra l'implementazione in Table.tsx.
        //
        // La scelta colonne invece resta: nascondere una colonna non cambia
        // QUALI righe si vedono, quindi non puo' mentire allo stesso modo.
        //
        // Per abilitarlo davvero serve l'ordinamento lato server
        // (`?order_by=&verso=` nell'orderBy di Prisma), non alzare il limite:
        // 200 sarebbe comunque un tetto, e oggi le righe sono gia' 265.
        sceltaColonne="auditlog"
        data={entries}
        rowKey={(e) => e.id}
        isLoading={isLoading}
        emptyMessage="Nessuna attività registrata."
      />

      {!isLoading && hasMore && (
        <div className="mt-4 flex justify-center">
          <Button variant="secondary" loading={isLoadingMore} onClick={loadMore}>
            Carica altre
          </Button>
        </div>
      )}
    </div>
  );
}
